// Pattern A (2026-05-15): CMS 管理画面から ADMIN が BUSINESS user を直接作成する Sheet UI。
//
// Spec: design/coupon-management-redesign-spec.md §B2B onboarding §Pattern A
//
// 既存 TierPresetFormSheet pattern 踏襲 (= 軽量 useState + 手動 validation)。
// auth: backend (`POST /system/business-users`) が ADMIN role のみ許可。
//
// 入力 fields: username (= ログイン ID) / email (= 連絡先 / SocialAuth キー) / password (= 8 chars+)
// 作成成功時に親 (= user 一覧) の reload を発火。

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    onSuccess?: () => void
}

export default observer(function CreateBusinessUserSheet({ open, onOpenChange, onSuccess }: Props) {
    const {
        userStore: { createBusinessUser }
    } = useStore();

    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            setUsername('');
            setEmail('');
            setPassword('');
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!username.trim()) {
            toastify('error', 'username は必須');
            return;
        }
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            toastify('error', '有効な email を入力してください');
            return;
        }
        if (password.length < 8) {
            toastify('error', 'password は 8 文字以上');
            return;
        }

        setSubmitting(true);
        try {
            const res = await flowResult(createBusinessUser({
                username: username.trim(),
                email: email.trim(),
                password
            }));
            if (res?.ok) {
                toastify('success', `事業者 user "${username}" を作成しました`);
                onOpenChange(false);
                onSuccess?.();
            } else {
                toastify('error', res?.message || '作成に失敗しました');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[480px] sm:max-w-[480px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>事業者を新規追加</SheetTitle>
                    <SheetDescription>
                        BUSINESS role の user を CMS から直接作成します (= 既存「事業者へ昇格」 は不要)。
                        credentials は事業者に LINE / 暗号化 email 等で渡してください。
                    </SheetDescription>
                </SheetHeader>

                <div className='space-y-4 py-4'>
                    <div>
                        <Label>
                            username (= ログイン ID)
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            placeholder='例: shop_a_owner'
                            maxLength={64}
                            autoComplete='off'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                            既存 username と重複不可。半角英数 / 記号、64 文字以下推奨。
                        </p>
                    </div>

                    <div>
                        <Label>
                            連絡先 email
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input
                            type='email'
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder='例: owner@shop-a.example'
                            autoComplete='off'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                            将来の月次レポート送信先。既存 email と重複不可。
                        </p>
                    </div>

                    <div>
                        <Label>
                            初期 password
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input
                            type='text'
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder='8 文字以上'
                            minLength={8}
                            autoComplete='new-password'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                            8 文字以上。生成後、事業者に伝達 + 初回ログイン後の変更を案内推奨。
                            (PoC 段階では password 変更 UI 未実装、必要なら ADMIN による DB 直接更新)
                        </p>
                    </div>

                    <div className='border border-amber-200 bg-amber-50 rounded p-3 text-xs text-amber-900'>
                        <p className='font-semibold mb-1'>注意事項</p>
                        <ul className='list-disc pl-4 space-y-1'>
                            <li>作成された BUSINESS は CMS にログインして自社 landmark / coupon_template を管理可能</li>
                            <li>enduser PWA への login は SocialAuth 未紐付けのため不可 (= 検証用に PWA で動作確認したい場合は別途「事業者へ昇格」 path を使用)</li>
                            <li>credential は本画面で 1 度だけ表示、生成後は user 自身が管理</li>
                        </ul>
                    </div>
                </div>

                <SheetFooter>
                    <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                        キャンセル
                    </Button>
                    <Button type='button' variant='active' onClick={handleSubmit} disabled={submitting}>
                        {submitting ? '作成中…' : '作成'}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
});
